import { useEffect, useRef, useState } from "react";

import { REJECTED_FILE_EXTENSIONS } from "./rejectedFileTypes";
import { validateUploadItem } from "./fileValidator";
import { debugLog } from "../../../configs/loggingConfig";

/**
 * A drag and drop file upload component that handles files, folders, and paste events
 * @param {Function} onFileUpload - Callback function that receives the uploaded files
 * @param {boolean} allowFolders - Whether to allow folder uploads
 * @param {string} displayText - Text to display in the upload button
 * @param {Function} onError - Callback for handling upload errors
 * @param {Function} onRejectedFiles - Callback for handling rejected files
 * @param {Array} rejectedFileTypes - Array of file extensions that should be rejected
 * @param {boolean} mergeBadFiles - Whether to merge error files and rejected files
 * @param {boolean} enablePaste - Whether to enable paste functionality
 * @param {boolean} documentListener - Whether to attach event listeners to document
 * @param {object} targetElementRef - useRef object for the element to attach event listeners to
 * @param {boolean} showUploader - Whether to show the default upload UI
 * @returns {JSX.Element} - Rendered component
 */
const DnDFileUpload = ({
    onFileUpload = null,
    allowFolders = true,
    //allowZips = false,
    //maxFileSize = null,
    displayText = 'Choose file or drag files here',
    onError = null,
    onRejectedFiles = null,
    rejectedFileTypes = REJECTED_FILE_EXTENSIONS,
    mergeBadFiles = true,
    enablePaste = true,
    documentListener = false,
    targetElementRef = null,
    showUploader = true,
}) => {
    const rejectedFilesRef = useRef([]);
    const errorFilesRef = useRef([]);
    const fileUploadInputRef = useRef(null);
    
    /**
     * Processes and uploads files after validation
     * @param {Array} items - Array of file items to process
     * @returns {Promise<void>}
     */
    async function handleFileUpload(items) {
        const rawFiles = [];
        errorFilesRef.current = [];
        for (const item of items) {
            try {
                
                //for pasted files
                if(item.kind === 'file' && item.getAsFile){
                    const file = item.getAsFile();
                    if(file){
                        rawFiles.push(file);
                        continue;
                    }
                }
                const getEntryMethod = item.getAsEntry || item.webkitGetAsEntry;
                
                if (getEntryMethod) {
                    const entry = getEntryMethod.call(item);
                    if (entry?.isDirectory) {
                        await handleDirectory(entry, rawFiles);
                    } else if (entry?.isFile) {
                        const file = await getFileFromEntry(entry);
                        rawFiles.push(file);
                    }
                } else {
                    const file = item.getAsFile();
                    if (file) {
                        rawFiles.push(file);
                    }
                }
            } catch (error) {
                debugLog.error("Error during file upload:", error);
                const errorFile = {
                    name: error.fileName || "Upload process",
                    reason: error.message || "Unknown error occurred"
                };

                errorFilesRef.current.push(errorFile);
            }
        }

        if (rawFiles.length > 0 && onFileUpload) {
            onFileUpload(rawFiles);
        }

        if (mergeBadFiles) {
            const allBadFiles = [...errorFilesRef.current, ...rejectedFilesRef.current];
            if (allBadFiles.length > 0) {
                handleRejectedFiles(allBadFiles);
            }
        } else {
            if (errorFilesRef.current.length > 0) {
                handleUploadError(errorFilesRef.current);
            }
            if (rejectedFilesRef.current.length > 0) {
                handleRejectedFiles(rejectedFilesRef.current);
            }
        }

    }

    /**
     * Handles rejected files by calling the onRejectedFiles callback
     * @param {Array} rejectedFiles - Array of rejected file objects
     */
    const handleRejectedFiles = (rejectedFiles) => {
        if (onRejectedFiles) {
            onRejectedFiles(rejectedFiles);
        }
    }

    /**
     * Handles upload errors by calling the onError callback
     * @param {Array} errorFiles - Array of error file objects
     */
    const handleUploadError = (errorFiles) => {
        if (onError) {
            onError(errorFiles);
        } 
    }

    /**
     * Recursively processes directories to extract files
     * @param {FileSystemDirectoryEntry} entry - Directory entry to process
     * @param {Array} rawFiles - Array to store extracted files
     * @returns {Promise<void>}
     */
    async function handleDirectory(entry, rawFiles) {
        const reader = entry.createReader();
        const entries = await new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject);
        });
       
        for (const entry of entries) {
            if(entry.isDirectory) {
                await handleDirectory(entry, rawFiles);
            } else if(entry.isFile) {
                const file = await new Promise((resolve, reject) => {
                    entry.file(resolve, reject);
                });
                
                rawFiles.push(file);
            }
        }
    }

    /**
     * Extracts a File object from a FileSystemFileEntry
     * @param {FileSystemFileEntry} entry - File entry to process
     * @returns {Promise<File>} - Promise that resolves to a File object
     */
    function getFileFromEntry(entry) {
        return new Promise((resolve, reject) => {
            entry.file(resolve, reject);
        });
    }

    /**
     * Extracts file items from various event types
     * @param {Event} event - Event containing file data
     * @returns {Array} - Array of file items
     */
    const getFileItems = (event) => {
        if (event.dataTransfer && event.dataTransfer.items) {
            return processTransferItems(event.dataTransfer.items);
        } else if (event.clipboardData && event.clipboardData.items) {
            return processTransferItems(event.clipboardData.items);
        } else {
            return Array.from(event.target.files || []).map(file => ({
            kind: "file",
            getAsFile: () => file,
            }));
        }
    }

    //essential for multi folder uploads
    //input is dataTransferItems
    function processTransferItems(items){
        return Array.from(items).map(item => {
            const file = item.getAsFile ? item.getAsFile() : null;
            const entry = item.getAsEntry ? item.getAsEntry : 
                            (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null);
            
            return {
                kind: item.kind,
                type: item.type,
                getAsFile: () => file,
                webkitGetAsEntry: () => entry,
                getAsEntry: () => entry
            }
        });
    }
    /**
     * Resets the file input element
     */
    const resetFileInput = () => {
        if(fileUploadInputRef.current){
            fileUploadInputRef.current.value = "";
        }
    }

    /**
     * Validates items against rejection criteria
     * @param {Array} items - Items to validate
     * @returns {Array} - Array of valid items
     */
    const validateInputItems = (items) => {
        const validItems = [];
        rejectedFilesRef.current = [];
        const allowZips = false;
        const maxFileSize = null; //maxFileSize || 1024 * 1024 * 10; (10MB) 
        
        for (const item of items) {
            if (item.kind === 'file') {
                if (validateUploadItem(item, allowFolders, allowZips, 
                    rejectedFilesRef.current, maxFileSize, rejectedFileTypes)) 
                {
                    validItems.push(item);
                }
            }
        }
        
        return validItems;
    }

    /**
     * Handles input events from file input, drop, or paste
     * @param {Event} e - Input event
     * @returns {Promise<void>}
     */
    const handleInput = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const fileItems = getFileItems(e);
        const validItems = validateInputItems(fileItems);
        await handleFileUpload(validItems);
        resetFileInput();
    }

    /**
     * Handles paste events to extract and process files
     * @param {ClipboardEvent} e - Paste event
     */
    const handlePaste = (e) => {
        if (e.clipboardData && e.clipboardData.items.length > 0) {
            const hasFiles = Array.from(e.clipboardData.items).some(item => item.kind === 'file');
            
            if (hasFiles) {
                handleInput(e);
            }
        }
    }

    /**
     * Handles file browser button clicks
     * @param {Event} e - Click event
     */
    const handleButtonClick = (e) => {
        e.preventDefault();
        if (fileUploadInputRef.current) {
            fileUploadInputRef.current.click();
        }
    }

    /**
     * Handles general drag events and updates drag state
     * @param {DragEvent} e - Drag event
     */
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    useEffect(() => {
        let targetElement;
        
        if (targetElementRef && targetElementRef.current) {
            targetElement = targetElementRef.current;
        } else if (documentListener) {
            targetElement = document;
        } else {
            return;
        }
                
        targetElement.addEventListener('dragenter', handleDrag);
        targetElement.addEventListener('dragover', handleDrag);
        targetElement.addEventListener('dragleave', handleDrag);
        targetElement.addEventListener('drop', handleInput);
        
        if (enablePaste) {
            targetElement.addEventListener('paste', handlePaste);
        }
    
        return () => {
            if (targetElement) {
                targetElement.removeEventListener('dragenter', handleDrag);
                targetElement.removeEventListener('dragover', handleDrag);
                targetElement.removeEventListener('dragleave', handleDrag);
                targetElement.removeEventListener('drop', handleInput);
                
                if (enablePaste) {
                    targetElement.removeEventListener('paste', handlePaste);
                }
            }
        };
    }, [enablePaste, documentListener, targetElementRef]);

    return (
        <>
            {showUploader && (
                <div 
                    className="m-file-uploader"
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleInput}
                >
                    <button 
                        className="m-file-uploader__dialog-btn"
                        onClick={handleButtonClick}
                    >
                        {displayText}
                    </button>
                    <input
                        ref={fileUploadInputRef}
                        type="file"
                        id="file"
                        style={{ display: 'none' }}
                        onChange={handleInput}
                        multiple
                    />
                </div>
            )}
        </>
    )
}
export default DnDFileUpload